import { db } from './codexDB';
import { SEED_TS } from '@/lib/constants';
import { CITIES, cityAlbedoPath, cityDepthPath } from '@/features/worlds/city/cities';
import { ATLAS_OWNED_NODES } from '@/features/worlds/atlas/atlasNodes';

/**
 * 도시 핵심 아트 시드 — 포지움 도시 컨셉아트를 `assets`(kind:'city' albedo · 'city-depth' optional)로 적재.
 * public/cities/{routeId}.png(+depth) → Blob → 결정적 id로 put(멱등). entityId = location(loc-*).
 * 버전(CITY_ART_VERSION) — 아트 교체 시 bump. **현재 버전 적재 성공 후에만 stale 삭제**(실패 시 옛 아트 보존 → blank 방지).
 * export 번들은 assets를 포함한다. 공개 시드는 새 DB/손상 에셋의 폴백으로도 도시 아트를 보장한다.
 */

const CITY_ART_VERSION = 1; // 도시 아트(albedo/depth) 내용 교체 시 +1

const albedoId = (locId: string) => `seed-city-v${CITY_ART_VERSION}-${locId}`;
const depthId = (locId: string) => `seed-city-depth-v${CITY_ART_VERSION}-${locId}`;

async function fetchPut(url: string, assetId: string, entityId: string, kind: 'city' | 'city-depth', i: number): Promise<boolean> {
  if (await db.assets.get(assetId)) return true; // 멱등(이미 적재됨)
  const res = await fetch(url);
  if (!res.ok) return false; // depth 없음 등
  const blob = await res.blob();
  if (!blob.size) return false;
  await db.assets.put({ id: assetId, entityId, kind, blob, mime: blob.type || 'image/png', name: assetId, createdAt: SEED_TS + i });
  return true;
}

export async function seedCities(): Promise<void> {
  if (typeof fetch === 'undefined') return; // jsdom 등 — skip
  for (const { routeId, locId } of CITIES) {
    if (ATLAS_OWNED_NODES.has(locId)) continue; // 아틀라스가 city 자산 단독 소유(충돌 방지)
    try {
      // 1) 현재 버전 먼저 적재(성공해야 stale 삭제 — 네트워크/put 실패 시 옛 아트 보존)
      const albedoOk = await fetchPut(cityAlbedoPath(routeId), albedoId(locId), locId, 'city', 0);
      const depthOk = await fetchPut(cityDepthPath(routeId), depthId(locId), locId, 'city-depth', 1);
      // 2) 옛 seed 정리는 **kind별로** 현재 버전 적재에 성공한 종류만(depth 일시 실패 시 옛 depth 보존). 사용자 업로드(다른 id)는 보존.
      const expected = new Set([albedoId(locId), depthId(locId)]);
      const stale = await db.assets
        .where('entityId')
        .equals(locId)
        .filter(
          (a) =>
            a.id.startsWith('seed-city') &&
            !expected.has(a.id) &&
            ((a.kind === 'city' && albedoOk) || (a.kind === 'city-depth' && depthOk)),
        )
        .toArray();
      if (stale.length) await db.assets.bulkDelete(stale.map((a) => a.id));
    } catch {
      /* 오프라인/테스트 — 다음 부팅 재시도 */
    }
  }
}
